import type { Block } from './types';

/**
 * Section anchors: every rendered section carries a stable id, so a Section menu
 * (blocks/SectionMenu.tsx) can jump to it, on the live site and in the Studio preview alike. The id
 * is the section's own id, which never changes when its words do. The menu lists a section under
 * the heading it SHOWS (its first heading on the page), so a hidden or empty section is never listed.
 */

/**
 * The element id a section is reachable at (`#section-<id>`). A block id is normally a plain
 * token ('blk-hero', a uuid) and is used as is; anything a link fragment can't carry (spaces,
 * quotes, '#', …) becomes '-', and an id with nothing usable left gives '' (the caller numbers it).
 */
export function sectionAnchorId(blockId: unknown): string {
  const token = (typeof blockId === 'string' || typeof blockId === 'number' ? String(blockId) : '')
    .trim()
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return token ? `section-${token}` : '';
}

/**
 * The anchor ids for a page's sections, in order, unique within the list. Block ids are meant to
 * be unique, but a copied or hand-edited page can repeat one; a repeat then gets a numbered suffix
 * (`section-a`, `section-a-2`, `section-a-3`, …) so no two sections share an element id. A section
 * with a missing or unusable id gets one from its place on the page (`section-3` for the third).
 * The same list always gets the same ids, so a link to the first copy never moves.
 */
export function sectionAnchorIds(blocks: ReadonlyArray<{ id?: unknown }>): string[] {
  const used = new Set<string>();
  return blocks.map((b, i) => {
    const base = sectionAnchorId(b?.id) || `section-${i + 1}`;
    let id = base;
    for (let n = 2; used.has(id); n++) id = `${base}-${n}`;
    used.add(id);
    return id;
  });
}

/** Sections a Section menu never lists: the site chrome, the menu itself and the hero at the top. */
export const UNLISTED_SECTION_TYPES: ReadonlySet<string> = new Set(['nav', 'footer', 'section_menu', 'hero']);

/** A section's heading from its props, for the Studio's list of sections ('' when it has none). */
export function sectionLabel(block: Pick<Block, 'type' | 'props'>): string {
  if (UNLISTED_SECTION_TYPES.has(block.type)) return '';
  const p = (block.props ?? {}) as Record<string, unknown>;
  for (const key of ['heading', 'title', 'headline']) {
    const v = p[key];
    if (typeof v === 'string' && v.trim()) return v.replace(/<[^>]*>/g, '').trim().slice(0, 60);
  }
  return '';
}
